import React, { useEffect, useRef, useState, useCallback } from 'react';
// Box removed to avoid TS2590
import cytoscape, { Core } from 'cytoscape';
// @ts-ignore
import fcose from 'cytoscape-fcose';
// @ts-ignore
import coseBilkent from 'cytoscape-cose-bilkent';
// @ts-ignore
import cytoscapeSvg from 'cytoscape-svg';
import { GraphNode, GraphEdge, NodeType, EdgeType } from '../../../types/graph.types';
import { cytoscapeConfig } from './cytoscape-config';
import { applyClustering, ClusterStrategy, Cluster, getUnclusteredNodes } from '../../../utils/graphClustering';
import {
  animateGraphUpdate,
  animateClusterExpand,
  animateClusterCollapse,
  prefersReducedMotion,
} from '../../../utils/graphAnimations';

// Register the layout algorithms
cytoscape.use(fcose);
cytoscape.use(coseBilkent);
cytoscape.use(cytoscapeSvg);

interface GraphCanvasProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  onNodeSelect?: (node: GraphNode) => void;
  onEdgeSelect?: (edge: GraphEdge) => void;
  selectedNode?: GraphNode | null;
  selectedEdge?: GraphEdge | null;
  searchResultNodeIds?: string[];
  clusterStrategy?: ClusterStrategy;
  onCyReady?: (cy: Core) => void;
  animationsEnabled?: boolean; // Control animations via feature flag
  annotationCounts?: Record<string, number>; // Node ID -> annotation count
}

/**
 * GraphCanvas Component
 * 
 * Renders an interactive knowledge graph using Cytoscape.js.
 * Supports various node types including future todo nodes.
 * 
 * Features:
 * - Interactive graph visualization
 * - Multiple layout algorithms
 * - Node and edge selection
 * - Zoom, pan, and navigation
 * - Hover effects and tooltips
 * - Export capabilities
 */
const GraphCanvas: React.FC<GraphCanvasProps> = ({
  nodes,
  edges,
  onNodeSelect,
  onEdgeSelect,
  selectedNode,
  selectedEdge,
  searchResultNodeIds = [],
  clusterStrategy = ClusterStrategy.CONVERSATION,
  onCyReady,
  animationsEnabled = true,
  annotationCounts = {},
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [, setClusters] = useState<Cluster[]>([]);
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());

  // Convert graph data to Cytoscape format with clustering support
  const convertToCytoscapeElements = useCallback(() => {
    // Add null checks for nodes and edges
    if (!nodes || !edges) {
      return [];
    }

    // Apply clustering if enabled
    const activeClusters = clusterStrategy !== ClusterStrategy.NONE
      ? applyClustering(nodes, clusterStrategy)
      : [];

    // Update clusters state
    setClusters(activeClusters);

    // Get unclustered nodes
    const unclusteredNodes = getUnclusteredNodes(nodes, activeClusters);

    // Create compound nodes for clusters (only if not expanded)
    const clusterNodes = activeClusters
      .filter(cluster => !expandedClusters.has(cluster.id))
      .map(cluster => ({
        data: {
          id: cluster.id,
          label: cluster.label,
          type: 'cluster',
          isCluster: true,
          nodeCount: cluster.nodeIds.length,
          strategy: cluster.strategy,
          metadata: cluster.metadata,
        },
        classes: 'cluster-node',
      }));

    // Create regular nodes (unclustered + nodes from expanded clusters)
    const regularNodes: any[] = [];

    // Add unclustered nodes
    unclusteredNodes.forEach(node => {
      regularNodes.push({
        data: {
          id: node.id,
          label: node.label,
          type: node.type,
          ...node.properties,
          metadata: node.metadata,
        },
        classes: `node-${node.type}`,
      });
    });

    // Add nodes from expanded clusters with parent assignment
    activeClusters.forEach(cluster => {
      if (expandedClusters.has(cluster.id)) {
        cluster.nodeIds.forEach(nodeId => {
          const node = nodes.find(n => n.id === nodeId);
          if (node) {
            regularNodes.push({
              data: {
                id: node.id,
                label: node.label,
                type: node.type,
                parent: cluster.id,  // Assign to parent cluster
                ...node.properties,
                metadata: node.metadata,
              },
              classes: `node-${node.type}`,
            });
          }
        });

        // Add compound node container for expanded cluster
        clusterNodes.push({
          data: {
            id: cluster.id,
            label: cluster.label,
            type: 'cluster',
            isCluster: true,
            nodeCount: cluster.nodeIds.length,
            strategy: cluster.strategy,
            metadata: cluster.metadata,
          },
          classes: 'cluster-node cluster-expanded',
        });
      }
    });

    const cytoscapeNodes = [...clusterNodes, ...regularNodes];

    // Create a set of existing node IDs for fast lookup
    const nodeIds = new Set(nodes.map(node => node.id));

    // Count nodes by type that GraphCanvas received
    const nodeTypeCount = nodes.reduce((acc: any, n: any) => {
      acc[n.type] = (acc[n.type] || 0) + 1;
      return acc;
    }, {});

    console.log('🔍 GRAPH CANVAS: Converting to Cytoscape format', {
      nodesCount: nodes.length,
      edgesCount: edges.length,
      nodeTypeCount,
      sampleNodeIds: Array.from(nodeIds).slice(0, 10),
      sampleEdges: edges.slice(0, 5).map(e => ({ source: e.source, target: e.target, type: e.type })),
      firstEmailNode: nodes.find((n: any) => n.type === 'email')?.id || 'NO EMAIL NODES',
      sampleNodeTypes: nodes.slice(0, 10).map((n: any) => ({ id: n.id, type: n.type }))
    });

    // Filter edges to only include those with valid source and target nodes
    const validEdges = edges.filter(edge => {
      const hasValidSource = nodeIds.has(edge.source);
      const hasValidTarget = nodeIds.has(edge.target);

      if (!hasValidSource || !hasValidTarget) {
        console.warn(`Skipping edge ${edge.id}: source ${edge.source} exists: ${hasValidSource}, target ${edge.target} exists: ${hasValidTarget}`);
        return false;
      }

      return true;
    });

    console.log('🔍 GRAPH CANVAS: Edge filtering complete', {
      totalEdges: edges.length,
      validEdges: validEdges.length,
      skippedEdges: edges.length - validEdges.length
    });

    const cytoscapeEdges = validEdges.map(edge => ({
      data: {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.label || edge.type,
        type: edge.type,
        weight: edge.weight || 1,
        metadata: edge.metadata,
      },
      classes: `edge-${edge.type}`,
    }));

    return [...cytoscapeNodes, ...cytoscapeEdges];
  }, [nodes, edges, clusterStrategy, expandedClusters]);

  // Initialize Cytoscape
  useEffect(() => {
    if (!containerRef.current || isInitialized) return;

    const cy = cytoscape({
      container: containerRef.current,
      elements: [],
      style: cytoscapeConfig.style,
      layout: { name: 'preset' },
      minZoom: 0.1,
      maxZoom: 3,
      wheelSensitivity: 0.2,
    });

    // Add event listeners
    cy.on('tap', 'node', (evt: any) => {
      const node = evt.target;
      const nodeData = node.data();

      // Handle cluster node expand/collapse with animation
      if (nodeData.isCluster) {
        const clusterId = nodeData.id;
        const isExpanded = expandedClusters.has(clusterId);
        const shouldAnimate = animationsEnabled && !prefersReducedMotion();

        if (shouldAnimate && cyRef.current) {
          if (isExpanded) {
            animateClusterCollapse(cyRef.current, clusterId).then(() => {
              setExpandedClusters(prev => {
                const newExpanded = new Set(prev);
                newExpanded.delete(clusterId);
                return newExpanded;
              });
            });
          } else {
            setExpandedClusters(prev => {
              const newExpanded = new Set(prev);
              newExpanded.add(clusterId);
              return newExpanded;
            });
            // Animation runs after re-render adds the child nodes
            setTimeout(() => {
              if (cyRef.current) {
                animateClusterExpand(cyRef.current, clusterId);
              }
            }, 100);
          }
        } else {
          setExpandedClusters(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(clusterId)) {
              newExpanded.delete(clusterId);
            } else {
              newExpanded.add(clusterId);
            }
            return newExpanded;
          });
        }
        return; // Don't select cluster nodes
      }

      // Regular node selection
      if (onNodeSelect) {
        const graphNode: GraphNode = {
          id: nodeData.id,
          type: nodeData.type,
          label: nodeData.label,
          namespace: nodeData.namespace || '',
          createdAt: new Date(nodeData.createdAt || Date.now()),
          updatedAt: new Date(nodeData.updatedAt || Date.now()),
          properties: nodeData,
          metadata: nodeData.metadata,
        };
        onNodeSelect(graphNode);
      }
    });

    cy.on('tap', 'edge', (evt: any) => {
      const edge = evt.target;
      const edgeData = edge.data();
      
      if (onEdgeSelect) {
        const graphEdge: GraphEdge = {
          id: edgeData.id,
          source: edgeData.source,
          target: edgeData.target,
          type: edgeData.type as EdgeType,
          label: edgeData.label,
          weight: edgeData.weight,
          metadata: edgeData.metadata,
          createdAt: new Date(edgeData.createdAt || Date.now()),
        };
        onEdgeSelect(graphEdge);
      }
    });

    // Hover effects
    cy.on('mouseover', 'node', (evt: any) => {
      const node = evt.target;
      node.addClass('hover');
      
      // Highlight connected edges and nodes
      node.connectedEdges().addClass('hover-connected');
      node.neighborhood().addClass('hover-neighbor');
    });

    cy.on('mouseout', 'node', (evt: any) => {
      const node = evt.target;
      node.removeClass('hover');
      node.connectedEdges().removeClass('hover-connected');
      node.neighborhood().removeClass('hover-neighbor');
    });

    // Double-click to expand node connections
    cy.on('dbltap', 'node', (evt: any) => {
      const node = evt.target;
      // This could trigger loading more connections for the node
      console.log('Double-clicked node:', node.id());
    });

    // Context menu (right-click)
    cy.on('cxttap', 'node', (evt: any) => {
      evt.preventDefault();
      evt.stopPropagation();
      // Could open a context menu here
      console.log('Right-clicked node:', evt.target.id());
    });

    cyRef.current = cy;
    setIsInitialized(true);

    // Notify parent that Cytoscape instance is ready
    if (onCyReady) {
      onCyReady(cy);
    }

    // Cleanup
    return () => {
      cy.destroy();
      cyRef.current = null;
      setIsInitialized(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Layout options used for graph rendering
  const layoutOptions = {
    name: 'fcose',
    quality: 'proof',
    randomize: false,
    animate: true,
    animationDuration: 1000,
    animationEasing: 'ease-out',
    fit: true,
    padding: 50,
    nodeDimensionsIncludeLabels: true,
    idealEdgeLength: 400,
    edgeElasticity: 0.45,
    nestingFactor: 0.1,
    gravity: 0.15,
    numIter: 2500,
    tile: true,
    tilingPaddingVertical: 60,
    tilingPaddingHorizontal: 60,
    nodeRepulsion: 16000,
    nodeOverlap: 40,
    improveFlow: true,
  };

  // Update graph data with animated transitions
  useEffect(() => {
    if (!cyRef.current || !isInitialized) return;

    const cy = cyRef.current;
    const elements = convertToCytoscapeElements();

    const shouldAnimate = animationsEnabled && !prefersReducedMotion();

    if (shouldAnimate && cy.elements().length > 0) {
      // Use animated graph update with element diffing
      animateGraphUpdate(cy, elements, layoutOptions).catch((err) => {
        console.warn('Graph animation failed, falling back to instant update:', err);
        cy.batch(() => {
          cy.elements().remove();
          cy.add(elements);
        });
        if (elements.length > 0) {
          cy.layout(layoutOptions as any).run();
        }
      });
    } else {
      // No animation: instant update (initial load or animations disabled)
      cy.batch(() => {
        cy.elements().remove();
        cy.add(elements);
      });

      if (elements.length > 0) {
        cy.layout(layoutOptions as any).run();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges, convertToCytoscapeElements, isInitialized, animationsEnabled]);

  // Apply annotation indicators to nodes
  useEffect(() => {
    if (!cyRef.current || !isInitialized) return;
    const cy = cyRef.current;

    cy.nodes().forEach((node) => {
      const count = annotationCounts[node.id()] || 0;
      if (count > 0) {
        node.addClass('has-annotations');
      } else {
        node.removeClass('has-annotations');
      }
    });
  }, [annotationCounts, isInitialized, nodes]);

  // Handle node selection highlighting
  useEffect(() => {
    if (!cyRef.current || !isInitialized) return;

    const cy = cyRef.current;
    
    // Clear previous selection
    cy.elements().removeClass('selected');

    // Highlight selected node
    if (selectedNode) {
      const node = cy.getElementById(selectedNode.id);
      if (node) {
        node.addClass('selected');
        
        // Center on selected node with animation
        cy.animate({
          center: { eles: node },
          zoom: 1.5,
          duration: 500,
        });
      }
    }

    // Highlight selected edge
    if (selectedEdge) {
      const edge = cy.getElementById(selectedEdge.id);
      if (edge) {
        edge.addClass('selected');
      }
    }
  }, [selectedNode, selectedEdge, isInitialized]);

  // Handle search result highlighting
  useEffect(() => {
    if (!cyRef.current || !isInitialized) return;

    const cy = cyRef.current;

    // Clear previous search highlights
    cy.elements().removeClass('search-match');

    // Highlight search result nodes
    if (searchResultNodeIds && searchResultNodeIds.length > 0) {
      console.log('🔍 GRAPH CANVAS: Highlighting search results', {
        resultCount: searchResultNodeIds.length,
        nodeIds: searchResultNodeIds
      });

      searchResultNodeIds.forEach(nodeId => {
        const node = cy.getElementById(nodeId);
        if (node && node.length > 0) {
          node.addClass('search-match');
        } else {
          console.warn(`🔍 GRAPH CANVAS: Node ${nodeId} not found for highlighting`);
        }
      });

      // Fit view to show all search results
      const matchedNodes = cy.nodes('.search-match');
      if (matchedNodes.length > 0) {
        console.log(`🔍 GRAPH CANVAS: Fitting view to ${matchedNodes.length} search results`);
        cy.animate({
          fit: { eles: matchedNodes, padding: 100 },
          duration: 800,
          easing: 'ease-out',
        });
      }
    }
  }, [searchResultNodeIds, isInitialized]);

  // Public methods exposed via ref
  useEffect(() => {
    if (!cyRef.current) return;

    // Expose graph manipulation methods
    (window as any).graphCanvas = {
      // Export as PNG
      exportPNG: () => {
        if (cyRef.current) {
          const png = cyRef.current.png({
            output: 'blob',
            bg: 'white',
            scale: 2,
          });
          return png;
        }
      },
      
      // Export as SVG
      exportSVG: () => {
        if (cyRef.current) {
          const svg = (cyRef.current as any).svg({
            bg: 'white',
            scale: 1,
          });
          return svg;
        }
      },

      // Change layout
      changeLayout: (layoutName: string) => {
        if (cyRef.current) {
          const layout = cyRef.current.layout({
            name: layoutName,
            animate: true,
            animationDuration: 1000,
            fit: true,
            padding: 50,
          } as any);
          layout.run();
        }
      },

      // Fit to viewport
      fit: () => {
        if (cyRef.current) {
          cyRef.current.fit(undefined, 50);
        }
      },

      // Reset zoom
      resetZoom: () => {
        if (cyRef.current) {
          cyRef.current.zoom(1);
          cyRef.current.center();
        }
      },

      // Filter nodes by type
      filterNodesByType: (types: NodeType[]) => {
        if (cyRef.current) {
          cyRef.current.batch(() => {
            // Hide all nodes first
            cyRef.current!.nodes().style('display', 'none');
            
            // Show nodes of selected types
            types.forEach(type => {
              cyRef.current!.nodes(`[type = "${type}"]`).style('display', 'element');
            });

            // Update edges visibility
            cyRef.current!.edges().style('display', (edge: any) => {
              const source = edge.source();
              const target = edge.target();
              return source.visible() && target.visible() ? 'element' : 'none';
            });
          });
        }
      },

      // Search and highlight
      searchAndHighlight: (searchTerm: string) => {
        if (cyRef.current && searchTerm) {
          cyRef.current.batch(() => {
            // Reset all highlighting
            cyRef.current!.elements().removeClass('search-match');
            
            // Find and highlight matching nodes
            const matches = cyRef.current!.nodes().filter((node: any) => {
              const label = node.data('label').toLowerCase();
              return label.includes(searchTerm.toLowerCase());
            });
            
            matches.addClass('search-match');
            
            // Fit to matching nodes if any
            if (matches.length > 0) {
              cyRef.current!.fit(matches, 100);
            }
          });
        }
      },
    };

    return () => {
      delete (window as any).graphCanvas;
    };
  }, [isInitialized]);

  return (
    <>
      <style>{`
        #graph-canvas .cy-tooltip {
          position: absolute;
          z-index: 1000;
          background-color: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          pointer-events: none;
        }
      `}</style>
      <div
        ref={containerRef}
        id="graph-canvas"
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          backgroundColor: '#f5f5f5',
        }}
      />
    </>
  );
};

export default GraphCanvas;